import {
    resolveConnectorEndpoint,
    resolveLayoutAnchorWorldPosition,
    type AnchorResolutionContext,
} from '@toolproof-core/visualization/_pure/entity-anchor-resolution';
import {
    deriveDimNonActiveOpacityByConstellationKey,
    deriveLinkedActiveMapFromActivationLinks,
} from '@toolproof-core/visualization/_pure/item-activation';
import {
    deriveOpacityPrecedence,
    resolveEntityItemOpacity,
} from '@toolproof-core/visualization/_pure/item-opacity';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { ConnectorItem, EntityItemParametricState } from '@toolproof-core/visualization/_contract/item';
import type {
    InstantFramePatch,
    InterpolatedFramePatch,
    SpaceDerivationFrame,
    TransitionPhase,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { COLOR_PALETTES } from '@/workflow/_data/config/fragments/colorPalettes';
import { deriveResourceTypeAngularInfo } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import {
    WORKFLOW_KEYS,
    buildToolStepOuterRimCollectionKey,
    buildToolStepOuterRimConstellationKey,
    buildToolStepOuterRimItemKey,
    parseResourceTypeRadialConnectorItemKey,
    parseWorkflowStateEntryItemKey,
} from '@/workflow/_pure/keyCodec';
import { parseToolStepKeyFromItemKey } from '@/workflow/_pure/parsing';
import { resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { derivePanelInputPotentialPropagationPhases } from '@/workflow-runner/_pure/derivation/panelInputPotentialPropagation';
import { deriveRingTransferPhases } from '@/workflow-runner/_pure/derivation/ringTransfer';
import { deriveStructuralGuideMotion, isStructuralGuide } from '@/workflow-runner/_pure/derivation/structuralGuideMotion';
import type {
    BranchExitRebindAnimationConfig,
    Config,
    LoopContinuationAnimationConfig,
} from '@/workflow-runner/_contract/config';
import type { PreItemTraceContext } from '@/workflow-runner/_contract/pipelineContext';
import type { PendingReplayMove } from '@/workflow-runner/_contract/runtime';
import { formatOperationCaption, type OperationCaptionModel } from './operationCaption';

type InterpolatedEntityItemByKey = NonNullable<
    NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']
>;

function collectItemKeys(itemCollections: readonly ItemCollection[]): Set<string> {
    const out = new Set<string>();
    for (const itemCollection of itemCollections) {
        for (const item of itemCollection.items) out.add(item.itemKey);
    }
    return out;
}

function deriveExistingToolStepOuterRimPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    rimItemKey: string | undefined;
}): InterpolatedFramePatch['itemParametricState'] | undefined {
    if (args.rimItemKey === undefined) return undefined;
    const baseState = readEntityItemState(args.base, args.rimItemKey);
    if (baseState?.shape?.shapeKind !== 'cylinder-wall') return undefined;

    const targetState = readEntityItemState(args.target, args.rimItemKey);
    const entityItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']> = {};
    if (targetState?.shape?.shapeKind !== 'cylinder-wall') {
        entityItemByKey[args.rimItemKey as keyof typeof entityItemByKey] = {
            visualStyle: { opacity: 0 },
        };
    } else if (
        baseState.shape.innerRadius !== targetState.shape.innerRadius
        || baseState.shape.outerRadius !== targetState.shape.outerRadius
    ) {
        entityItemByKey[args.rimItemKey as keyof typeof entityItemByKey] = {
            shape: {
                innerRadius: targetState.shape.innerRadius,
                outerRadius: targetState.shape.outerRadius,
                height: targetState.shape.height,
            },
        } as NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']>[keyof NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']>];
    }
    if (Object.keys(entityItemByKey).length === 0) return undefined;
    return { entityItemByKey };
}

function findCollection(
    itemCollections: readonly ItemCollection[],
    itemCollectionKey: string | undefined,
): ItemCollection | undefined {
    if (itemCollectionKey === undefined) return undefined;
    return itemCollections.find((itemCollection) => itemCollection.itemCollectionKey === itemCollectionKey);
}

function withOpacity(
    state: EntityItemParametricState | undefined,
    opacity: number,
): EntityItemParametricState {
    return {
        ...(state ?? {}),
        visualStyle: {
            ...(state?.visualStyle ?? {}),
            opacity,
        },
    };
}

function deriveRemovedOuterRimCleanupPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    rimCollectionKey: string | undefined;
}): InstantFramePatch | undefined {
    if (
        args.rimCollectionKey === undefined
        || findCollection(args.base.itemCollections, args.rimCollectionKey) === undefined
        || findCollection(args.target.itemCollections, args.rimCollectionKey) !== undefined
    ) return undefined;

    return {
        itemCollections: args.target.itemCollections,
        constellations: args.target.constellations,
        ...(args.target.constellationParametricState === undefined
            ? {}
            : { constellationParametricState: args.target.constellationParametricState }),
    };
}

function readEntityItemState(frame: SpaceDerivationFrame, itemKey: string): EntityItemParametricState | undefined {
    return frame.itemParametricState?.entityItemByKey?.[itemKey as keyof NonNullable<typeof frame.itemParametricState.entityItemByKey>];
}

function deriveCausingOutputPanelFillPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    preItemTraceContext: PreItemTraceContext;
    activeToolStepItemKey?: `item:entity:${string}`;
}): InstantFramePatch['itemParametricState'] | undefined {
    const activeToolStepItemKey = args.activeToolStepItemKey ?? (args.preItemTraceContext.toolStepConstellationKey === undefined
        ? undefined
        : args.target.spaceRenderOptions?.activation?.activeItemKeyByConstellationKey?.[args.preItemTraceContext.toolStepConstellationKey]);
    if (!activeToolStepItemKey) return undefined;

    const causingStepKey = parseToolStepKeyFromItemKey(activeToolStepItemKey);
    if (!causingStepKey) return undefined;

    const baseItemKeys = collectItemKeys(args.base.itemCollections);

    const entityItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};

    for (const [itemKey, targetState] of Object.entries(args.target.itemParametricState?.entityItemByKey ?? {})) {
        if (!baseItemKeys.has(itemKey)) continue;
        const parsed = parseWorkflowStateEntryItemKey(itemKey);
        if (!parsed) continue;
        if (parsed.stepKey !== causingStepKey) continue;
        if (parsed.direction !== 'output') continue;
        if (targetState?.shape?.shapeKind !== 'panel') continue;

        entityItemByKey[itemKey as keyof typeof entityItemByKey] = {
            shape: {
                appearanceMode: targetState.shape.appearanceMode ?? 'filled',
            },
            ...(targetState.label === undefined ? {} : { label: targetState.label }),
        } as NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>[keyof NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>];
    }

    if (Object.keys(entityItemByKey).length === 0) return undefined;
    return { entityItemByKey };
}

function deriveInsertedItemPatches(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    preserveGuides?: boolean;
}): {
    insertPatch: InstantFramePatch['itemParametricState'];
    fadePatch: InterpolatedFramePatch['itemParametricState'];
} {
    const baseItemKeys = collectItemKeys(args.base.itemCollections);
    const insertedEntityItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
    const fadeEntityItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']> = {};
    const insertedConnectorItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    const fadeConnectorItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};

    for (const itemCollection of args.target.itemCollections) {
        for (const item of itemCollection.items) {
            if (baseItemKeys.has(item.itemKey)) continue;

            if (item.itemKind === 'entity') {
                const targetState = args.target.itemParametricState?.entityItemByKey?.[item.itemKey];
                insertedEntityItemByKey[item.itemKey] = targetState?.shape?.shapeKind === 'panel'
                    ? {
                        ...targetState,
                        visualStyle: {
                            ...(targetState.visualStyle ?? {}),
                            opacity: 0,
                        },
                    }
                    : withOpacity(targetState, 0);
                fadeEntityItemByKey[item.itemKey] = {
                    visualStyle: {
                        opacity: targetState?.visualStyle?.opacity ?? 1,
                    },
                };
                continue;
            }

            const targetState = args.target.itemParametricState?.connectorItemByKey?.[item.itemKey];
            if (args.preserveGuides && isStructuralGuide(itemCollection, item, targetState)) {
                insertedConnectorItemByKey[item.itemKey] = { ...targetState, endpointPositions: null };
                continue;
            }
            insertedConnectorItemByKey[item.itemKey] = {
                ...(targetState ?? {}),
                visualStyle: {
                    ...(targetState?.visualStyle ?? {}),
                    opacity: 0,
                },
            };
            fadeConnectorItemByKey[item.itemKey] = {
                visualStyle: {
                    opacity: targetState?.visualStyle?.opacity ?? 1,
                },
            };
        }
    }

    return {
        insertPatch: {
            ...(Object.keys(insertedEntityItemByKey).length === 0 ? {} : { entityItemByKey: insertedEntityItemByKey }),
            ...(Object.keys(insertedConnectorItemByKey).length === 0 ? {} : { connectorItemByKey: insertedConnectorItemByKey }),
        },
        fadePatch: {
            ...(Object.keys(fadeEntityItemByKey).length === 0 ? {} : { entityItemByKey: fadeEntityItemByKey }),
            ...(Object.keys(fadeConnectorItemByKey).length === 0 ? {} : { connectorItemByKey: fadeConnectorItemByKey }),
        } as InterpolatedFramePatch['itemParametricState'],
    };
}

function deriveRemovedItemFadePatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    preserveGuides?: boolean;
}): InterpolatedFramePatch['itemParametricState'] {
    const targetItemKeys = collectItemKeys(args.target.itemCollections);
    const entityItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']> = {};
    const connectorItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};

    for (const itemCollection of args.base.itemCollections) {
        for (const item of itemCollection.items) {
            if (targetItemKeys.has(item.itemKey)) continue;

            if (item.itemKind === 'entity') {
                entityItemByKey[item.itemKey] = { visualStyle: { opacity: 0 } };
                continue;
            }

            if (!args.preserveGuides || !isStructuralGuide(itemCollection, item,
                args.base.itemParametricState?.connectorItemByKey?.[item.itemKey])) {
                connectorItemByKey[item.itemKey] = { visualStyle: { opacity: 0 } };
            }
        }
    }

    return {
        ...(Object.keys(entityItemByKey).length === 0 ? {} : { entityItemByKey }),
        ...(Object.keys(connectorItemByKey).length === 0 ? {} : { connectorItemByKey }),
    } as InterpolatedFramePatch['itemParametricState'];
}

function deriveRemovedItemFadeStartPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
}): InstantFramePatch['itemParametricState'] | undefined {
    const targetItemKeys = collectItemKeys(args.target.itemCollections);
    const activation = args.target.spaceRenderOptions?.activation ?? args.base.spaceRenderOptions?.activation;
    const active = activation?.activeItemKeyByConstellationKey ?? {};
    const linked = deriveLinkedActiveMapFromActivationLinks({
        directActiveItemKeyByConstellationKey: active,
        activationLinks: activation?.activationLinks,
    });
    const entityConstellations = args.base.constellations.filter((constellation) => constellation.constellationKind === 'entity');
    const dimming = deriveDimNonActiveOpacityByConstellationKey({
        entityConstellations,
        entityConstellationParametricStateByKey: args.base.constellationParametricState?.entityConstellationByKey,
        dimming: activation?.nonActiveItemDimming,
        effectiveActiveItemKeyByConstellationKey: active,
        effectiveLinkedActiveItemKeysByConstellationKey: linked,
    });
    const entityItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
    for (const constellation of entityConstellations) {
        const collection = findCollection(args.base.itemCollections, constellation.itemCollectionKey);
        if (collection?.itemCollectionKind !== 'entity') continue;
        for (const item of collection.items) {
            if (targetItemKeys.has(item.itemKey)) continue;
            const state = args.base.itemParametricState?.entityItemByKey?.[item.itemKey];
            const opacity = deriveOpacityPrecedence({
                itemParametricState: state,
                baseItemOpacity: resolveEntityItemOpacity(state),
                activeItemKey: active[constellation.constellationKey],
                currentItemKey: item.itemKey,
                linkedActiveItemKeys: linked[constellation.constellationKey],
                dimNonActiveOpacity: dimming[constellation.constellationKey],
            }).resolvedOpacity;
            entityItemByKey[item.itemKey] = { visualStyle: { opacity } };
        }
    }
    return Object.keys(entityItemByKey).length === 0 ? undefined : { entityItemByKey };
}

function deriveSharedOutputPanelPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
}): InstantFramePatch['itemParametricState'] | undefined {
    const targetItemKeys = collectItemKeys(args.target.itemCollections);
    const entityItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};

    for (const itemKey of collectItemKeys(args.base.itemCollections)) {
        if (!targetItemKeys.has(itemKey)) continue;
        const targetState = readEntityItemState(args.target, itemKey);
        const parsed = parseWorkflowStateEntryItemKey(itemKey);
        if (!parsed || parsed.direction !== 'output') continue;
        if (targetState?.shape?.shapeKind !== 'panel') continue;

        entityItemByKey[itemKey as keyof typeof entityItemByKey] = {
            shape: {
                appearanceMode: targetState.shape.appearanceMode ?? 'outlined',
            },
            ...(targetState.label === undefined ? {} : { label: targetState.label }),
        } as NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>[keyof NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>];
    }

    return Object.keys(entityItemByKey).length === 0 ? undefined : { entityItemByKey };
}

export function deriveWorkflowStateEntryLabelRemovals(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
}): InstantFramePatch['entityItemLabelChannelsToRemoveByKey'] {
    const removals: NonNullable<InstantFramePatch['entityItemLabelChannelsToRemoveByKey']> = {};
    const targetItemKeys = collectItemKeys(args.target.itemCollections);
    for (const itemKey of collectItemKeys(args.base.itemCollections)) {
        if (!targetItemKeys.has(itemKey)) continue;
        const parsed = parseWorkflowStateEntryItemKey(itemKey);
        if (!parsed) continue;
        const targetState = readEntityItemState(args.target, itemKey);
        if (targetState?.shape?.shapeKind !== 'panel') continue;
        const baseLabel = readEntityItemState(args.base, itemKey)?.label;
        const channels = (['aboveItem', 'onItem'] as const).filter(
            (channel) => baseLabel?.[channel] !== undefined && targetState.label?.[channel] === undefined,
        );
        if (channels.length > 0) removals[itemKey as keyof typeof removals] = channels;
    }
    return Object.keys(removals).length > 0 ? removals : undefined;
}

function deriveSharedConnectorReanchorPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    preserveGuides?: boolean;
}): InstantFramePatch['itemCollections'] | undefined {
    const targetConnectorByKey = new Map<string, ItemCollection['items'][number]>();
    for (const targetCollection of args.target.itemCollections) {
        if (targetCollection.itemCollectionKind !== 'connector') continue;
        for (const targetItem of targetCollection.items) targetConnectorByKey.set(targetItem.itemKey, targetItem);
    }

    let changed = false;
    const itemCollections = args.base.itemCollections.map((baseCollection) => {
        if (baseCollection.itemCollectionKind !== 'connector') return baseCollection;

        let collectionChanged = false;
        const items = baseCollection.items.map((baseItem) => {
            if (args.preserveGuides && isStructuralGuide(baseCollection, baseItem,
                args.base.itemParametricState?.connectorItemByKey?.[baseItem.itemKey])) return baseItem;
            const targetItem = targetConnectorByKey.get(baseItem.itemKey);
            if (!targetItem || targetItem.itemKind !== 'connector') return baseItem;
            if (JSON.stringify(baseItem.endpoints) === JSON.stringify(targetItem.endpoints)) return baseItem;
            changed = true;
            collectionChanged = true;
            return targetItem;
        });

        return collectionChanged ? { ...baseCollection, items } : baseCollection;
    });

    return changed ? itemCollections : undefined;
}

function deriveInsertedTopologyWithSourceSharedConnectors(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    settleGuides?: boolean;
}): ItemCollection[] {
    const baseConnectorByKey = new Map<string, ItemCollection['items'][number]>();
    for (const baseCollection of args.base.itemCollections) {
        if (baseCollection.itemCollectionKind !== 'connector') continue;
        for (const baseItem of baseCollection.items) baseConnectorByKey.set(baseItem.itemKey, baseItem);
    }

    return args.target.itemCollections.map((targetCollection) => {
        if (targetCollection.itemCollectionKind !== 'connector') return targetCollection;

        const items = targetCollection.items.map((targetItem) => {
            if (args.settleGuides && isStructuralGuide(targetCollection, targetItem,
                args.target.itemParametricState?.connectorItemByKey?.[targetItem.itemKey])) return targetItem;
            const baseItem = baseConnectorByKey.get(targetItem.itemKey);
            return baseItem?.itemKind === 'connector' ? baseItem : targetItem;
        });
        return { ...targetCollection, items };
    });
}

function readConcentricRingLayout(
    frame: SpaceDerivationFrame,
    toolStepConstellationKey: string | undefined,
) {
    const layout = frame.constellationParametricState?.entityConstellationByKey?.[
        toolStepConstellationKey as keyof NonNullable<NonNullable<typeof frame.constellationParametricState>['entityConstellationByKey']>
    ]?.layout;
    return layout?.layoutKind === 'concentric-ring' ? layout : undefined;
}

function deriveRingRadiusByItemKey(
    itemCollection: ItemCollection,
    layout: { ringRadii?: readonly number[] | undefined; startRadius: number; ringGap: number },
): Map<string, number> {
    const radiusByItemKey = new Map<string, number>();
    itemCollection.items.forEach((item, index) => {
        const radius = layout.ringRadii?.[index] ?? (layout.startRadius + (index * layout.ringGap));
        if (Number.isFinite(radius)) radiusByItemKey.set(item.itemKey, radius);
    });
    return radiusByItemKey;
}

// shapeSource selects the topology the emitted array must stay index-compatible with:
// 'base' when the phase runs before insert/remove, 'target' when it runs after.
function deriveToolStepRingRadiiPatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    toolStepCollectionKey: string | undefined;
    toolStepConstellationKey: string | undefined;
    shapeSource: 'base' | 'target';
}): InterpolatedFramePatch['constellationParametricState'] | undefined {
    const baseCollection = findCollection(args.base.itemCollections, args.toolStepCollectionKey);
    const targetCollection = findCollection(args.target.itemCollections, args.toolStepCollectionKey);
    if (!baseCollection || !targetCollection) return undefined;

    const baseLayout = readConcentricRingLayout(args.base, args.toolStepConstellationKey);
    const targetLayout = readConcentricRingLayout(args.target, args.toolStepConstellationKey);
    if (!baseLayout || !targetLayout) return undefined;

    const baseRadiusByItemKey = deriveRingRadiusByItemKey(baseCollection, baseLayout);
    const targetRadiusByItemKey = deriveRingRadiusByItemKey(targetCollection, targetLayout);

    const shapeCollection = args.shapeSource === 'base' ? baseCollection : targetCollection;
    const projectedRingRadii = shapeCollection.items.map((item) => (
        targetRadiusByItemKey.get(item.itemKey) ?? baseRadiusByItemKey.get(item.itemKey) ?? Number.NaN
    ));
    if (projectedRingRadii.some((radius) => !Number.isFinite(radius))) return undefined;

    return ({
        entityConstellationByKey: {
            [args.toolStepConstellationKey ?? '']: {
                layout: {
                    ringRadii: projectedRingRadii,
                },
            },
        },
    } as unknown) as InterpolatedFramePatch['constellationParametricState'];
}

// Items that only exist in the target enter topology here, so they carry no effective parametric
// state yet and would render with renderer defaults (full opacity, default color) instead of their
// configured appearance.
function deriveTargetOnlyItemParametricState(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
}): InstantFramePatch['itemParametricState'] | undefined {
    const baseItemKeys = collectItemKeys(args.base.itemCollections);
    const targetItemKeys = collectItemKeys(args.target.itemCollections);

    const pickTargetOnly = <T,>(record: Partial<Record<string, T>> | undefined): Partial<Record<string, T>> => {
        const out: Partial<Record<string, T>> = {};
        for (const [itemKey, state] of Object.entries(record ?? {})) {
            if (state === undefined) continue;
            if (baseItemKeys.has(itemKey) || !targetItemKeys.has(itemKey)) continue;
            out[itemKey] = state as T;
        }
        return out;
    };

    const entityItemByKey = pickTargetOnly(args.target.itemParametricState?.entityItemByKey);
    const connectorItemByKey = pickTargetOnly(args.target.itemParametricState?.connectorItemByKey);

    if (Object.keys(entityItemByKey).length === 0 && Object.keys(connectorItemByKey).length === 0) return undefined;

    return ({
        ...(Object.keys(entityItemByKey).length === 0 ? {} : { entityItemByKey }),
        ...(Object.keys(connectorItemByKey).length === 0 ? {} : { connectorItemByKey }),
    } as unknown) as InstantFramePatch['itemParametricState'];
}

// Replaces topology with the target's. Parametric state for surviving items is deliberately not
// written here: they keep their source values so the later contract and outline phases still have
// somewhere to travel from, and removed items are pruned by normalization. Only the ringRadii array
// is resized, because interpolated arrays must stay index-compatible with the new topology.
function deriveRemovalPhasePatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    toolStepCollectionKey: string | undefined;
    toolStepConstellationKey: string | undefined;
    rimCollectionKey: string | undefined;
    rimConstellationKey: string | undefined;
}): InstantFramePatch {
    const baseCollection = findCollection(args.base.itemCollections, args.toolStepCollectionKey);
    const targetCollection = findCollection(args.target.itemCollections, args.toolStepCollectionKey);
    const baseLayout = readConcentricRingLayout(args.base, args.toolStepConstellationKey);
    const targetLayout = readConcentricRingLayout(args.target, args.toolStepConstellationKey);

    const heldRingRadii = (() => {
        if (!baseCollection || !targetCollection || !baseLayout || !targetLayout) return undefined;
        const baseRadiusByItemKey = deriveRingRadiusByItemKey(baseCollection, baseLayout);
        const targetRadiusByItemKey = deriveRingRadiusByItemKey(targetCollection, targetLayout);
        const radii = targetCollection.items.map((item) => (
            baseRadiusByItemKey.get(item.itemKey) ?? targetRadiusByItemKey.get(item.itemKey) ?? Number.NaN
        ));
        return radii.some((radius) => !Number.isFinite(radius)) ? undefined : radii;
    })();

    const holdOuterRim = args.rimCollectionKey !== undefined
        && args.rimConstellationKey !== undefined
        && findCollection(args.base.itemCollections, args.rimCollectionKey) !== undefined
        && findCollection(args.target.itemCollections, args.rimCollectionKey) === undefined;
    const constellationParametricState = (() => {
        const targetState = args.target.constellationParametricState;
        if (
            (heldRingRadii === undefined || args.toolStepConstellationKey === undefined || !targetLayout)
            && !holdOuterRim
        ) {
            return targetState as InstantFramePatch['constellationParametricState'];
        }

        const entityConstellationByKey = {
            ...((targetState?.entityConstellationByKey ?? {}) as Record<string, Record<string, unknown>>),
        };
        if (heldRingRadii !== undefined && args.toolStepConstellationKey !== undefined && targetLayout) {
            entityConstellationByKey[args.toolStepConstellationKey] = {
                ...(entityConstellationByKey[args.toolStepConstellationKey] ?? {}),
                layout: { ...targetLayout, ringRadii: heldRingRadii },
            };
        }
        if (holdOuterRim && args.rimConstellationKey !== undefined) {
            const baseRimState = args.base.constellationParametricState?.entityConstellationByKey?.[
                args.rimConstellationKey as keyof NonNullable<typeof args.base.constellationParametricState.entityConstellationByKey>
            ];
            if (baseRimState !== undefined) entityConstellationByKey[args.rimConstellationKey] = baseRimState as Record<string, unknown>;
        }

        return ({
            ...(targetState ?? {}),
            entityConstellationByKey,
        } as unknown) as InstantFramePatch['constellationParametricState'];
    })();

    const itemParametricState = deriveTargetOnlyItemParametricState({ base: args.base, target: args.target });
    const rimCollection = holdOuterRim && args.rimCollectionKey !== undefined
        ? findCollection(args.base.itemCollections, args.rimCollectionKey)
        : undefined;
    const rimConstellation = holdOuterRim && args.rimConstellationKey !== undefined
        ? args.base.constellations.find((constellation) => constellation.constellationKey === args.rimConstellationKey)
        : undefined;

    return {
        itemCollections: rimCollection === undefined
            ? args.target.itemCollections
            : [...args.target.itemCollections, rimCollection],
        constellations: rimConstellation === undefined
            ? args.target.constellations
            : [...args.target.constellations, rimConstellation],
        ...(itemParametricState === undefined ? {} : { itemParametricState }),
        ...(constellationParametricState === undefined ? {} : { constellationParametricState }),
    };
}


function deriveExistingCaseShapePatch(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
}): InterpolatedFramePatch['itemParametricState'] | undefined {
    const baseItemKeys = collectItemKeys(args.base.itemCollections);
    const entityItemByKey: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']> = {};

    for (const itemKey of baseItemKeys) {
        const baseState = readEntityItemState(args.base, itemKey);
        const targetState = readEntityItemState(args.target, itemKey);
        if (baseState?.shape?.shapeKind === 'revolved-profile'
            && targetState?.shape?.shapeKind === 'revolved-profile') {
            if (baseState.shape.profile.length !== targetState.shape.profile.length) {
                throw new Error('Case envelope transitions require matching profile vertex counts.');
            }
            entityItemByKey[itemKey as keyof typeof entityItemByKey] = {
                shape: { profile: targetState.shape.profile },
            };
            continue;
        }
        if (baseState?.shape?.shapeKind !== 'annular-band') continue;
        if (targetState?.shape?.shapeKind !== 'annular-band') continue;

        if (
            baseState.shape.innerRadius === targetState.shape.innerRadius
            && baseState.shape.outerRadius === targetState.shape.outerRadius
        ) {
            continue;
        }

        entityItemByKey[itemKey as keyof typeof entityItemByKey] = {
            shape: {
                innerRadius: targetState.shape.innerRadius,
                outerRadius: targetState.shape.outerRadius,
            },
        } as NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']>[keyof NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['entityItemByKey']>];
    }

    if (Object.keys(entityItemByKey).length === 0) return undefined;
    return { entityItemByKey };
}

function deepMergeRecord<T extends Record<string, unknown>>(left?: T, right?: T): T | undefined {
    if (!left && !right) return undefined;
    if (!left) return right;
    if (!right) return left;
    const out: Record<string, unknown> = { ...left };
    for (const [key, value] of Object.entries(right)) {
        if (value === undefined) continue;
        const prev = out[key];
        if (
            prev !== undefined
            && prev !== null
            && typeof prev === 'object'
            && !Array.isArray(prev)
            && typeof value === 'object'
            && !Array.isArray(value)
        ) {
            out[key] = deepMergeRecord(
                prev as Record<string, unknown>,
                value as Record<string, unknown>,
            );
        } else {
            out[key] = value;
        }
    }
    return out as T;
}

function mergeInterpolatedFramePatches(
    left: InterpolatedFramePatch,
    right: InterpolatedFramePatch,
): InterpolatedFramePatch {
    const itemParametricState = deepMergeRecord(left.itemParametricState, right.itemParametricState);
    const constellationParametricState = deepMergeRecord(
        left.constellationParametricState,
        right.constellationParametricState,
    );
    const entityConstellationLocalRotationDeltaByKey = deepMergeRecord(
        left.entityConstellationLocalRotationDeltaByKey,
        right.entityConstellationLocalRotationDeltaByKey,
    );
    const entityConstellationOrbitByKey = deepMergeRecord(
        left.entityConstellationOrbitByKey,
        right.entityConstellationOrbitByKey,
    );
    const spaceRenderOptions = deepMergeRecord(left.spaceRenderOptions, right.spaceRenderOptions);

    return {
        ...(itemParametricState !== undefined ? { itemParametricState } : {}),
        ...(constellationParametricState !== undefined ? { constellationParametricState } : {}),
        ...(entityConstellationLocalRotationDeltaByKey !== undefined ? { entityConstellationLocalRotationDeltaByKey } : {}),
        ...(entityConstellationOrbitByKey !== undefined ? { entityConstellationOrbitByKey } : {}),
        ...(spaceRenderOptions !== undefined ? { spaceRenderOptions } : {}),
    };
}

function deriveRimOriginNearestPositionPatch(
    rimConstellationKey: ReturnType<typeof buildToolStepOuterRimConstellationKey>,
    position: readonly [number, number, number],
): InterpolatedFramePatch {
    const entityConstellationByKey: Partial<Record<
        ReturnType<typeof buildToolStepOuterRimConstellationKey>,
        { layout?: { originNearestPosition?: readonly [number, number, number] } }
    >> = {};
    entityConstellationByKey[rimConstellationKey] = {
        layout: { originNearestPosition: position },
    };
    return { constellationParametricState: { entityConstellationByKey } };
}

function hasInterpolatedPatchContent(patch: InterpolatedFramePatch): boolean {
    return Object.keys(patch.itemParametricState?.entityItemByKey ?? {}).length > 0
        || Object.keys(patch.itemParametricState?.connectorItemByKey ?? {}).length > 0
        || Object.keys(patch.constellationParametricState?.entityConstellationByKey ?? {}).length > 0
        || Object.keys(patch.constellationParametricState?.connectorConstellationByKey ?? {}).length > 0;
}

// Phases play sequentially, so spaceShiftPhaseEnd must leave a real share of the budget for the fade.
function derivePhaseDurations(animationConfig: {
    transitionDurationMs: number;
    spaceShiftPhaseEnd?: number;
}): {
    spaceShiftDurationMs: number;
    fadeDurationMs: number;
} {
    const totalDurationMs = Math.max(0, animationConfig.transitionDurationMs);
    const spaceShiftEnd = Math.max(0, Math.min(1, animationConfig.spaceShiftPhaseEnd ?? 0.6));
    const spaceShiftDurationMs = totalDurationMs * spaceShiftEnd;

    return {
        spaceShiftDurationMs,
        fadeDurationMs: totalDurationMs - spaceShiftDurationMs,
    };
}

function deriveLoopContinuationPhaseDurations(config: Config) {
    return derivePhaseDurations(config.adapterModeConfig.playback.loopContinuationAnimation);
}

function deriveBranchRebindPhaseDurations(config: Config) {
    return derivePhaseDurations(
        config.adapterModeConfig.playback.branchExitRebindAnimation
        ?? config.adapterModeConfig.playback.loopContinuationAnimation
    );
}

function deriveTransitionKey(pendingReplayMove: PendingReplayMove): string {
    return [
        pendingReplayMove.eventKind,
        pendingReplayMove.direction,
        pendingReplayMove.fromPositionIndex,
        pendingReplayMove.toPositionIndex,
    ].join(':');
}

function derivePanelGuideMotion(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    config: Config;
    preItemTraceContext: PreItemTraceContext;
}, expanding: boolean) {
    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    if (resolveOperationPresentationMode(args.config) !== 'panels'
        || rimItemKey === undefined
        || readEntityItemState(args.base, rimItemKey)?.shape?.shapeKind !== 'cylinder-wall'
        || readEntityItemState(args.target, rimItemKey)?.shape?.shapeKind !== 'cylinder-wall') return undefined;
    return deriveStructuralGuideMotion({
        base: args.base,
        target: args.target,
        toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
        toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
        rimItemKey,
        expanding,
    });
}

function resolveTriggerAngle(base: SpaceDerivationFrame, config: Config): number {
    const placement = config.domainPresentation?.entities?.resourceTypes?.angularPlacement;
    if (placement?.placementKind === 'explicit' && placement.angleByResourceTypeHandle?.['TYPE-Boolean'] !== undefined) {
        return placement.angleByResourceTypeHandle['TYPE-Boolean'];
    }
    const info = deriveResourceTypeAngularInfo({ itemCollections: base.itemCollections, config });
    const angle = info.angleByHandle.get('TYPE-Boolean');
    if (angle !== undefined && Number.isFinite(angle)) return angle;
    return (5 * Math.PI) / 4;
}

function splitScaffoldPatch(
    scaffoldPatch: InterpolatedFramePatch,
    rimItemKey: string | undefined,
): { rimPatch: InterpolatedFramePatch; innerPatch: InterpolatedFramePatch } {
    if (
        rimItemKey === undefined
        || !scaffoldPatch.itemParametricState?.entityItemByKey?.[rimItemKey as keyof typeof scaffoldPatch.itemParametricState.entityItemByKey]
    ) {
        return { rimPatch: {}, innerPatch: scaffoldPatch };
    }

    const entityItemByKey = scaffoldPatch.itemParametricState.entityItemByKey;
    const rimState = entityItemByKey[rimItemKey as keyof typeof entityItemByKey];
    const otherEntityItems = { ...entityItemByKey };
    delete otherEntityItems[rimItemKey as keyof typeof otherEntityItems];

    const rimPatch: InterpolatedFramePatch = {
        itemParametricState: {
            entityItemByKey: {
                [rimItemKey as keyof typeof entityItemByKey]: rimState,
            },
        },
    };

    const hasOtherEntityItems = Object.keys(otherEntityItems).length > 0;
    const hasConnectors = scaffoldPatch.itemParametricState.connectorItemByKey !== undefined
        && Object.keys(scaffoldPatch.itemParametricState.connectorItemByKey).length > 0;

    const innerItemParametricState: InterpolatedFramePatch['itemParametricState'] = (hasOtherEntityItems || hasConnectors)
        ? {
            ...(hasOtherEntityItems ? { entityItemByKey: otherEntityItems } : {}),
            ...(hasConnectors ? { connectorItemByKey: scaffoldPatch.itemParametricState.connectorItemByKey } : {}),
        }
        : undefined;

    const innerPatch: InterpolatedFramePatch = {
        ...(scaffoldPatch.constellationParametricState !== undefined
            ? { constellationParametricState: scaffoldPatch.constellationParametricState }
            : {}),
        ...(innerItemParametricState !== undefined
            ? { itemParametricState: innerItemParametricState }
            : {}),
    };

    return { rimPatch, innerPatch };
}

function appendScaffoldPhases(args: {
    phases: TransitionPhase[];
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    config: Config;
    scaffoldPatch: InterpolatedFramePatch;
    durationMs: number;
    transitionKey: string;
    preItemTraceContext: PreItemTraceContext;
    animationConfig?: LoopContinuationAnimationConfig | BranchExitRebindAnimationConfig;
}): void {
    if (args.durationMs <= 0) {
        args.phases.push({
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: args.transitionKey,
                duration: 0,
            },
            patch: args.scaffoldPatch,
        });
        return;
    }

    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    const rimConstellationKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimConstellationKey(args.preItemTraceContext.workflowKeyScope);

    const baseRimState = rimItemKey === undefined ? undefined : readEntityItemState(args.base, rimItemKey);
    const targetRimState = rimItemKey === undefined ? undefined : readEntityItemState(args.target, rimItemKey);

    const baseRimConstellation = rimConstellationKey === undefined
        ? undefined
        : args.base.constellationParametricState?.entityConstellationByKey?.[rimConstellationKey];
    const baseRimLayout = baseRimConstellation?.layout;
    const hasOriginNearest = baseRimLayout?.layoutKind === 'spoke'
        && Array.isArray(baseRimLayout.originNearestPosition);
    const baseRimShape = baseRimState?.shape;
    const targetRimShape = targetRimState?.shape;

    const eccentricConfig = args.animationConfig?.eccentricLead;
    const leadEnabled = eccentricConfig?.enabled ?? false;

    if (
        baseRimShape?.shapeKind !== 'cylinder-wall'
        || targetRimShape?.shapeKind !== 'cylinder-wall'
        || !hasOriginNearest
        || Math.abs(
            (targetRimShape.innerRadius + targetRimShape.outerRadius) / 2
            - (baseRimShape.innerRadius + baseRimShape.outerRadius) / 2,
        ) <= 1e-4
        || !leadEnabled
        || rimConstellationKey === undefined
        || rimItemKey === undefined
    ) {
        args.phases.push({
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: args.transitionKey,
                duration: args.durationMs,
            },
            patch: args.scaffoldPatch,
        });
        return;
    }

    const baseCenterline = (baseRimShape.innerRadius + baseRimShape.outerRadius) / 2;
    const targetCenterline = (targetRimShape.innerRadius + targetRimShape.outerRadius) / 2;
    const deltaR = targetCenterline - baseCenterline;
    const isExpansion = deltaR > 0;

    const leadFactor = eccentricConfig?.leadFactor ?? 1.0;
    const splitFactor = Math.max(0.05, Math.min(0.95, eccentricConfig?.splitFactor ?? 0.45));
    const colorPulse = eccentricConfig?.colorPulse ?? true;

    const triggerAngle = resolveTriggerAngle(args.base, args.config);
    const leadDistance = leadFactor * deltaR;
    const leadOffset: [number, number, number] = [
        leadDistance * Math.cos(triggerAngle),
        0,
        leadDistance * Math.sin(triggerAngle),
    ];

    const leadDurationMs = Math.max(1, Math.round(args.durationMs * splitFactor));
    const settleDurationMs = Math.max(1, args.durationMs - leadDurationMs);

    const palette = COLOR_PALETTES[args.config.colorPalette] ?? COLOR_PALETTES.alpha;
    const activeColor = palette.activation.active;
    const restingColor = targetRimState?.visualStyle?.color
        ?? baseRimState?.visualStyle?.color
        ?? palette.toolSteps.outerRim;
    const restingOpacity = targetRimState?.visualStyle?.opacity
        ?? baseRimState?.visualStyle?.opacity
        ?? args.config.domainPresentation?.entities?.toolSteps?.outerRim?.opacity
        ?? 0.82;

    const { rimPatch, innerPatch } = splitScaffoldPatch(args.scaffoldPatch, rimItemKey);

    if (colorPulse) {
        args.phases.push({
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `${args.transitionKey}:lead:pulse-on`,
            },
            patch: {
                itemParametricState: {
                    entityItemByKey: {
                        [rimItemKey as keyof InterpolatedEntityItemByKey]: {
                            visualStyle: {
                                color: activeColor,
                                opacity: 1.0,
                            },
                        },
                    },
                },
            },
        });
    }

    const leadPatch = isExpansion
        ? mergeInterpolatedFramePatches(
            rimPatch,
            deriveRimOriginNearestPositionPatch(rimConstellationKey, leadOffset),
        )
        : mergeInterpolatedFramePatches(
            innerPatch,
            deriveRimOriginNearestPositionPatch(rimConstellationKey, leadOffset),
        );

    args.phases.push({
        transitionOptions: {
            transitionKind: 'interpolated',
            transitionKey: `${args.transitionKey}:lead`,
            duration: leadDurationMs,
        },
        patch: leadPatch,
    });

    if (colorPulse) {
        args.phases.push({
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `${args.transitionKey}:settle:pulse-restore`,
            },
            patch: {
                itemParametricState: {
                    entityItemByKey: {
                        [rimItemKey as keyof InterpolatedEntityItemByKey]: {
                            visualStyle: {
                                color: restingColor,
                            },
                        },
                    },
                },
            },
        });
    }

    const settlePatch = isExpansion
        ? mergeInterpolatedFramePatches(
            innerPatch,
            {
                ...deriveRimOriginNearestPositionPatch(rimConstellationKey, [0, 0, 0]),
                ...(colorPulse ? {
                    itemParametricState: {
                        entityItemByKey: {
                            [rimItemKey as keyof InterpolatedEntityItemByKey]: {
                                visualStyle: {
                                    opacity: restingOpacity,
                                },
                            },
                        },
                    },
                } : {}),
            },
        )
        : mergeInterpolatedFramePatches(
            rimPatch,
            {
                ...deriveRimOriginNearestPositionPatch(rimConstellationKey, [0, 0, 0]),
                ...(colorPulse ? {
                    itemParametricState: {
                        entityItemByKey: {
                            [rimItemKey as keyof InterpolatedEntityItemByKey]: {
                                visualStyle: {
                                    opacity: restingOpacity,
                                },
                            },
                        },
                    },
                } : {}),
            },
        );

    args.phases.push({
        transitionOptions: {
            transitionKind: 'interpolated',
            transitionKey: `${args.transitionKey}:settle`,
            duration: settleDurationMs,
        },
        patch: settlePatch,
    });
}

function resolveRadialPath(args: {
    connector: ConnectorItem;
    context: AnchorResolutionContext;
}): { from: number[]; delta: number[]; length: number } | undefined {
    const from = resolveConnectorEndpoint({ endpoint: args.connector.endpoints.from, context: args.context })?.position;
    const to = resolveConnectorEndpoint({ endpoint: args.connector.endpoints.to, context: args.context })?.position;
    if (!from || !to) return undefined;
    const delta = to.map((value, axis) => value - from[axis]);
    const length = Math.hypot(...delta);
    return Number.isFinite(length) && length > 0 ? { from, delta, length } : undefined;
}

function deriveRadialEntryDistances(args: {
    panels: Array<{ itemKey: string }>;
    path: { from: number[]; delta: number[]; length: number };
    context: AnchorResolutionContext;
    reverse: boolean;
}): Array<{ itemKey: string; distance: number }> {
    const lengthSquared = args.path.length * args.path.length;
    return args.panels.flatMap(({ itemKey }) => {
        const position = resolveLayoutAnchorWorldPosition({
            anchor: { anchorKind: 'item', anchorItemKey: itemKey as `item:entity:${string}` },
            context: args.context,
        });
        if (!position) return [];
        const projection = position.reduce((sum, value, axis) => (
            sum + (value - args.path.from[axis]) * args.path.delta[axis]
        ), 0) / lengthSquared;
        const fraction = Math.max(0, Math.min(1, args.reverse ? 1 - projection : projection));
        return [{ itemKey, distance: fraction * args.path.length }];
    });
}

/**
 * Plays the presentation-only radial pulse around one logical ToolStep.
 * The replay trace and materialized workflow state remain owned by the runner.
 */
export function deriveResourceTypeRadialActivationWavePhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    geometryFrame?: SpaceDerivationFrame;
    pendingReplayMove: PendingReplayMove;
    config: Config;
    preItemTraceContext: PreItemTraceContext;
    activeToolStepItemKey: `item:entity:${string}` | undefined;
    transitionKey: string;
    stage?: 'input-hold' | 'output-materialize' | 'output-clear';
    captionModel?: OperationCaptionModel;
}): TransitionPhase[] {
    if (resolveOperationPresentationMode(args.config) !== 'panels') return [];
    const toolStepConstellationKey = args.preItemTraceContext.toolStepConstellationKey;
    if (!toolStepConstellationKey) return [];
    const activeToolStepItemKey = args.activeToolStepItemKey;
    const toolStepKey = activeToolStepItemKey
        ? parseToolStepKeyFromItemKey(activeToolStepItemKey)
        : undefined;
    if (!toolStepKey) return [];
    const geometryFrame = args.geometryFrame ?? args.target;
    const context: AnchorResolutionContext = {
        entityConstellations: geometryFrame.constellations.filter((entry) => entry.constellationKind === 'entity'),
        entityItemsByCollectionKey: new Map(geometryFrame.itemCollections.flatMap((collection) => (
            collection.itemCollectionKind === 'entity' ? [[collection.itemCollectionKey, collection.items] as const] : []
        ))),
        itemParametricStateByKey: geometryFrame.itemParametricState?.entityItemByKey ?? {},
        entityConstellationParametricStateByKey: geometryFrame.constellationParametricState?.entityConstellationByKey ?? {},
    };

    const radialItems = geometryFrame.itemCollections.flatMap((collection) => (
        collection.itemCollectionKind === 'connector'
            ? collection.items.flatMap((item) => {
                const parsed = parseResourceTypeRadialConnectorItemKey(item.itemKey);
                return parsed?.toolStepKey === toolStepKey ? [{ itemKey: item.itemKey, connector: item, ...parsed }] : [];
            })
            : []
    ));
    if (radialItems.length === 0) return [];
    const toolItemKeys = new Set(
        (args.target.spaceRenderOptions?.activation?.activationLinks
            ?? args.base.spaceRenderOptions?.activation?.activationLinks ?? [])
            .filter((link) => link.sourceConstellationKey === toolStepConstellationKey
                && link.targetConstellationKey === WORKFLOW_KEYS.constellations.tool)
            .flatMap((link) => link.targetItemKeysBySourceItemKey[activeToolStepItemKey!] ?? []),
    );
    if (toolItemKeys.size !== 1) throw new Error('Radial execution requires exactly one Tool definition for its ToolStep.');
    const toolItemKey = [...toolItemKeys][0]!;

    const panelsBySideAndResourceType = new Map<string, Array<{ itemKey: string }>>();
    for (const collection of geometryFrame.itemCollections) {
        if (collection.itemCollectionKind !== 'entity') continue;
        for (const item of collection.items) {
            const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
            if (!parsed || parsed.stepKey !== toolStepKey) continue;
            const key = `${parsed.direction}:${parsed.resourceTypeHandle}`;
            const group = panelsBySideAndResourceType.get(key) ?? [];
            group.push({ itemKey: item.itemKey });
            panelsBySideAndResourceType.set(key, group);
        }
    }
    const occupiedSegments = new Set(panelsBySideAndResourceType.keys());
    // Outputs can materialize after the wave starts in the source geometry.
    for (const collection of args.target.itemCollections) {
        if (collection.itemCollectionKind !== 'entity') continue;
        for (const item of collection.items) {
            const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
            if (parsed?.stepKey === toolStepKey && parsed.direction === 'output') {
                occupiedSegments.add(`output:${parsed.resourceTypeHandle}`);
            }
        }
    }
    const hasEntries = (item: typeof radialItems[number]) => occupiedSegments.has(
        `${item.direction}:${item.resourceTypeItemKey.slice('item:entity:'.length)}`,
    );

    const activeColor = COLOR_PALETTES[args.config.colorPalette].activation.active;
    const wave = args.config.adapterModeConfig.playback.resourceTypeRadialActivation;
    const ripple = args.config.adapterModeConfig.playback.resourceTypeRadialRipple;
    const transitionOptions = (suffix: string) => ({
        transitionKind: 'interpolated' as const,
        transitionKey: `${args.transitionKey}:resource-type-radial:${suffix}`,
    });
    const phases: TransitionPhase[] = [];
    let phaseIndex = 0;
    const materializedInputs = new Set<string>();
    const sideOrder = args.stage === 'input-hold' ? ['input'] as const
        : args.stage === 'output-clear' || args.stage === 'output-materialize' ? ['output'] as const
        : args.pendingReplayMove.direction === 'forward'
        ? ['input', 'output'] as const
        : ['output', 'input'] as const;
    const progressDirection = args.pendingReplayMove.direction === 'forward' ? 'from-to' : 'to-from';

    const connectorItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    for (const item of radialItems) {
        if (!hasEntries(item)) {
            connectorItemByKey[item.itemKey] = { progressiveActivation: null };
            continue;
        }
        let progress01 = (args.stage === 'output-clear' || args.stage === 'output-materialize') && item.direction === 'input' ? 1 : 0;
        if (args.stage === 'output-clear' && item.direction === 'output') {
            const path = resolveRadialPath({ connector: item.connector, context });
            if (path) progress01 = Math.max(0, ...deriveRadialEntryDistances({
                panels: panelsBySideAndResourceType.get(`output:${item.resourceTypeItemKey.slice('item:entity:'.length)}`) ?? [],
                path, context, reverse: false,
            }).map(({ distance }) => distance / path.length));
        }
        connectorItemByKey[item.itemKey] = {
            progressiveActivation: {
                progress01,
                direction: progressDirection, color: activeColor,
                ...(ripple.enabled && ripple.amplitude > 0 ? { ripple: {
                    amplitude: ripple.amplitude,
                    pulseLength: ripple.pulseLength,
                    oscillationCount: ripple.oscillationCount,
                } } : {}),
            },
        };
    }
    const sourceActivation = args.base.spaceRenderOptions?.activation;
    const sourceActivationLinks = sourceActivation?.activationLinks?.map((link) => {
        if (link.sourceConstellationKey !== WORKFLOW_KEYS.constellations.tool
            || link.targetConstellationKey !== toolStepConstellationKey) return link;
        // Execution activates the definition, not every invocation of that Tool.
        const targetItemKeysBySourceItemKey = { ...link.targetItemKeysBySourceItemKey };
        delete targetItemKeysBySourceItemKey[toolItemKey];
        return { ...link, targetItemKeysBySourceItemKey };
    });
    const sourceActiveItemKeyByConstellationKey: NonNullable<NonNullable<SpaceDerivationFrame['spaceRenderOptions']>['activation']>['activeItemKeyByConstellationKey'] = {
        ...sourceActivation?.activeItemKeyByConstellationKey,
        [WORKFLOW_KEYS.constellations.tool]: toolItemKey,
    };
    if ((args.stage === 'output-clear' || args.stage === 'output-materialize') && activeToolStepItemKey !== undefined) {
        sourceActiveItemKeyByConstellationKey[toolStepConstellationKey] = activeToolStepItemKey;
    } else delete sourceActiveItemKeyByConstellationKey[toolStepConstellationKey];
    const highlightedPanels = new Set<string>();
    const revealedCaptionItems = new Set<string>();
    let captionRunning = args.pendingReplayMove.direction === 'reverse'
        || args.stage === 'output-clear' || args.stage === 'output-materialize';
    if (args.captionModel && captionRunning) {
        for (const operand of args.captionModel.inputs) revealedCaptionItems.add(operand.itemKey);
        if (args.stage === 'output-clear' || args.pendingReplayMove.direction === 'reverse') {
            for (const operand of args.captionModel.outputs) revealedCaptionItems.add(operand.itemKey);
        }
    }
    const captionPatch = () => args.config.adapterModeConfig.showOperationCaption && args.captionModel
        ? { operationCaption: formatOperationCaption(args.captionModel, revealedCaptionItems, captionRunning) } : {};
    const transferEndpoints = (side: 'input' | 'output') => radialItems.flatMap((item) => {
        if (item.direction !== side || !(panelsBySideAndResourceType.get(
            `${side}:${item.resourceTypeItemKey.slice('item:entity:'.length)}`)?.length)) return [];
        const position = resolveConnectorEndpoint({
            endpoint: side === 'input' ? item.connector.endpoints.to : item.connector.endpoints.from, context,
        })?.position;
        return position ? [{ position, type: item.resourceTypeItemKey.slice('item:entity:'.length) }] : [];
    });
    const inputTransfer = transferEndpoints('input')[0];
    const outputTransfer = transferEndpoints('output')[0];
    const fromTransfer = args.pendingReplayMove.direction === 'forward' ? inputTransfer : outputTransfer;
    const toTransfer = args.pendingReplayMove.direction === 'forward' ? outputTransfer : inputTransfer;
    const transfer = (args.stage === undefined || args.stage === 'output-materialize')
        && fromTransfer && toTransfer && activeToolStepItemKey ? deriveRingTransferPhases({
        frame: geometryFrame, config: args.config, stepKey: toolStepKey, itemKey: activeToolStepItemKey,
        from: fromTransfer.position, to: toTransfer.position, destinationType: toTransfer.type,
        color: activeColor, transitionKey: args.transitionKey,
    }) : [];
    const heldPanels: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
    if (args.stage === 'output-clear' || args.stage === 'output-materialize') {
        for (const [key, panels] of panelsBySideAndResourceType) {
            if (args.stage === 'output-materialize' && !key.startsWith('input:')) continue;
            for (const { itemKey } of panels) {
                highlightedPanels.add(itemKey);
                heldPanels[itemKey as keyof typeof heldPanels] = { visualStyle: { color: activeColor } };
            }
        }
    }
    phases.push({
        transitionOptions: {
            transitionKind: 'instant',
            transitionKey: `${args.transitionKey}:resource-type-radial:${args.stage === 'output-clear' ? 'resume'
                : args.stage === 'output-materialize' ? 'materialize' : 'start'}`,
        },
        patch: {
            itemParametricState: {
                connectorItemByKey,
                ...(args.stage === 'output-clear' || args.stage === 'output-materialize' ? { entityItemByKey: heldPanels } : {}),
            },
            spaceRenderOptions: {
                activation: {
                    ...sourceActivation,
                    activationLinks: sourceActivationLinks,
                    activeItemKeyByConstellationKey: sourceActiveItemKeyByConstellationKey,
                },
                ...captionPatch(),
            },
        },
    });

    if (args.stage === 'output-materialize') phases.push(...transfer);
    const materializedOutput = args.pendingReplayMove.direction === 'forward'
        && (args.stage === undefined || args.stage === 'output-materialize') ? deriveCausingOutputPanelFillPatch({
        base: args.base, target: args.target, preItemTraceContext: args.preItemTraceContext, activeToolStepItemKey,
    })?.entityItemByKey : undefined;
    for (const side of sideOrder) {
        const sideRadialItems = radialItems.filter((item) => item.direction === side);
        if (sideRadialItems.length === 0) continue;
        const sidePaths = sideRadialItems.flatMap((item) => {
            const path = resolveRadialPath({ connector: item.connector, context });
            if (path === undefined) return [];
            const panelDistances = deriveRadialEntryDistances({
                panels: panelsBySideAndResourceType.get(
                    `${side}:${item.resourceTypeItemKey.slice('item:entity:'.length)}`
                ) ?? [],
                path, context, reverse: args.pendingReplayMove.direction === 'reverse',
            });
            const panelEnd = Math.max(0, ...panelDistances.map(({ distance }) => distance));
            return [{
                item,
                path,
                panelDistances,
                startDistance: args.stage === 'output-clear' ? panelEnd : 0,
                endDistance: args.stage === 'output-materialize' ? panelEnd : path.length,
            }];
        });
        if (sidePaths.length === 0) continue;
        const maximumLength = Math.max(...sidePaths.map(({ path }) => path.length));
        const travelDistances = Array.from(new Set([
            ...sidePaths.flatMap(({ panelDistances, startDistance, endDistance }) => panelDistances
                .filter(({ distance }) => (args.stage === 'output-clear' ? distance > startDistance : distance >= startDistance)
                    && distance <= endDistance)
                .map(({ distance }) => distance - startDistance)),
            Math.max(...sidePaths.map(({ startDistance, endDistance }) => endDistance - startDistance)),
        ])).sort((left, right) => left - right);
        const durationMs = side === 'input' ? wave.inputDurationMs : wave.outputDurationMs;
        let previousDistance = 0;
        const lastPanelDistance = Math.max(0, ...sidePaths.flatMap(({ panelDistances, startDistance }) =>
            panelDistances.map(({ distance }) => distance - startDistance)));
        let outputHeld = false;

        for (const distance of travelDistances) {
            const connectorProgress: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};
            for (const { item, path, startDistance, endDistance } of sidePaths) {
                if (!hasEntries(item)) continue;
                connectorProgress[item.itemKey] = {
                    progressiveActivation: { progress01: Math.min(startDistance + distance, endDistance) / path.length },
                };
            }
            phases.push({
                transitionOptions: {
                    ...transitionOptions(`${side}:${phaseIndex}`),
                    duration: durationMs * ((distance - previousDistance) / maximumLength),
                },
                patch: { itemParametricState: { connectorItemByKey: connectorProgress } },
            });
            previousDistance = distance;

            const crossedPanels = sidePaths.flatMap(({ panelDistances, startDistance }) => panelDistances
                .map((entry) => ({ ...entry, distance: entry.distance - startDistance })))
                .filter((entry) => entry.distance <= distance && !highlightedPanels.has(entry.itemKey));
            if (crossedPanels.length > 0) {
                const entityItemByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
                for (const entry of crossedPanels) {
                    highlightedPanels.add(entry.itemKey);
                    if (args.pendingReplayMove.direction === 'forward') revealedCaptionItems.add(entry.itemKey);
                    else revealedCaptionItems.delete(entry.itemKey);
                    entityItemByKey[entry.itemKey as keyof typeof entityItemByKey] = {
                        ...materializedOutput?.[entry.itemKey as keyof NonNullable<typeof materializedOutput>],
                        visualStyle: { color: activeColor },
                    };
                }
                phases.push({
                    transitionOptions: {
                        transitionKind: 'instant',
                        transitionKey: `${args.transitionKey}:resource-type-radial:${side}:entries:${phaseIndex}`,
                    },
                    patch: { itemParametricState: { entityItemByKey }, spaceRenderOptions: captionPatch() },
                });
                if (side === 'output' && materializedOutput) {
                    const propagation = derivePanelInputPotentialPropagationPhases({
                        frame: geometryFrame, target: args.target, sources: entityItemByKey,
                        config: args.config, color: activeColor,
                        transitionKey: `${args.transitionKey}:resource-type-radial:dependency:${phaseIndex}`,
                        previouslyMaterialized: materializedInputs,
                    });
                    phases.push(...propagation.phases);
                    propagation.materializedKeys.forEach((key) => materializedInputs.add(key));
                }
            }
            if (!outputHeld && side === 'output' && args.pendingReplayMove.direction === 'forward'
                && args.stage !== 'output-clear' && distance >= lastPanelDistance
                && sidePaths.some(({ panelDistances }) => panelDistances.length > 0)) {
                outputHeld = true;
                if (wave.postOutputHoldMs > 0) phases.push({
                    transitionOptions: {
                        transitionKind: 'instant',
                        transitionKey: `${args.transitionKey}:resource-type-radial:post-output-hold`,
                        delay: wave.postOutputHoldMs,
                    },
                    patch: {},
                });
            }
            phaseIndex += 1;
        }

        if (side === (args.pendingReplayMove.direction === 'forward' ? 'input' : 'output')) {
            const targetActivation = args.target.spaceRenderOptions?.activation;
            if (targetActivation !== undefined && activeToolStepItemKey !== undefined) {
                captionRunning = args.pendingReplayMove.direction === 'forward';
                phases.push({
                    transitionOptions: {
                        transitionKind: 'instant',
                        transitionKey: `${args.transitionKey}:resource-type-radial:ring`,
                    },
                    patch: {
                        spaceRenderOptions: {
                            ...captionPatch(),
                            activation: {
                                ...targetActivation,
                                activeItemKeyByConstellationKey: {
                                    ...targetActivation.activeItemKeyByConstellationKey,
                                    [toolStepConstellationKey]: activeToolStepItemKey,
                                    [WORKFLOW_KEYS.constellations.tool]: toolItemKey,
                                },
                            },
                        },
                    },
                });
            }
            if (transfer.length > 0 && args.stage !== 'input-hold') {
                phases.push(...transfer);
            } else if (wave.processingHoldMs > 0) {
                const holdConnectorState: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};
                for (const item of sideRadialItems) {
                    if (!hasEntries(item)) continue;
                    holdConnectorState[item.itemKey] = { progressiveActivation: { progress01: 1 } };
                }
                phases.push({
                    transitionOptions: {
                        ...transitionOptions('processing-hold'),
                        duration: wave.processingHoldMs,
                    },
                    patch: { itemParametricState: { connectorItemByKey: holdConnectorState } },
                });
            }
        }
    }

    if (args.stage === 'input-hold' || args.stage === 'output-materialize') return phases;

    const connectorClearByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    for (const item of radialItems) connectorClearByKey[item.itemKey] = { progressiveActivation: null };
    const entityRestoreByKey: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
    for (const itemKey of highlightedPanels) {
        const targetState = args.target.itemParametricState?.entityItemByKey?.[itemKey as keyof NonNullable<typeof args.target.itemParametricState.entityItemByKey>];
        if (targetState !== undefined) entityRestoreByKey[itemKey as keyof typeof entityRestoreByKey] = targetState;
    }
    const activation = args.target.spaceRenderOptions?.activation;
    const activeItemKeyByConstellationKey = { ...activation?.activeItemKeyByConstellationKey };
    delete activeItemKeyByConstellationKey[toolStepConstellationKey];
    delete activeItemKeyByConstellationKey[WORKFLOW_KEYS.constellations.tool];
    const clearedActivation = activation === undefined
        ? undefined
        : {
            ...activation,
            ...(Object.keys(activeItemKeyByConstellationKey).length > 0
                ? { activeItemKeyByConstellationKey }
                : {}),
        };
    if (clearedActivation && Object.keys(activeItemKeyByConstellationKey).length === 0) {
        delete clearedActivation.activeItemKeyByConstellationKey;
    }
    phases.push({
        transitionOptions: {
            transitionKind: 'instant',
            transitionKey: `${args.transitionKey}:resource-type-radial:clear`,
        },
        patch: {
            itemParametricState: {
                connectorItemByKey: connectorClearByKey,
                entityItemByKey: entityRestoreByKey,
            },
            spaceRenderOptions: {
                ...(clearedActivation === undefined ? {} : { activation: clearedActivation }),
                ...(args.captionModel && args.pendingReplayMove.direction === 'reverse'
                    ? { operationCaption: args.target.spaceRenderOptions?.operationCaption ?? null } : {}),
            },
        },
    });
    return phases;
}

export function deriveLoopContinuationForwardTransitionPhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    pendingReplayMove: PendingReplayMove & { eventKind: 'loop_continuation'; direction: 'forward' };
    config: Config;
    preItemTraceContext: PreItemTraceContext;
    activeToolStepItemKey?: `item:entity:${string}`;
}): TransitionPhase[] | undefined {
    const phases: TransitionPhase[] = [];
    const transitionKey = deriveTransitionKey(args.pendingReplayMove);
    const guideMotion = derivePanelGuideMotion(args, true);
    const { spaceShiftDurationMs: expandDurationMs, fadeDurationMs: fadeInDurationMs } = deriveLoopContinuationPhaseDurations(args.config);
    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    const fillPatch = deriveCausingOutputPanelFillPatch({
        base: args.base,
        target: args.target,
        preItemTraceContext: args.preItemTraceContext,
        activeToolStepItemKey: args.activeToolStepItemKey,
    });
    if (fillPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
                patch: {
                itemParametricState: fillPatch,
            },
        });
    }

    const expandPatch = mergeInterpolatedFramePatches(
        {
            constellationParametricState: deriveToolStepRingRadiiPatch({
                base: args.base,
                target: args.target,
                toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
                toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
                shapeSource: 'base',
            }),
        },
        {
            itemParametricState: deriveExistingCaseShapePatch({
                base: args.base,
                target: args.target,
            }),
        },
    );
    const expandWithRimPatch = mergeInterpolatedFramePatches(expandPatch, {
        itemParametricState: deriveExistingToolStepOuterRimPatch({
            base: args.base,
            target: args.target,
            rimItemKey,
        }),
    });

    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.startPatch });
    const expandingScaffoldPatch = mergeInterpolatedFramePatches(expandWithRimPatch, guideMotion?.motionPatch ?? {});
    if (hasInterpolatedPatchContent(expandingScaffoldPatch)) {
        appendScaffoldPhases({
            phases,
            base: args.base,
            target: args.target,
            config: args.config,
            scaffoldPatch: expandingScaffoldPatch,
            durationMs: expandDurationMs,
            transitionKey,
            preItemTraceContext: args.preItemTraceContext,
            animationConfig: args.config.adapterModeConfig.playback.loopContinuationAnimation,
        });
    }

    const insertedPatches = deriveInsertedItemPatches({
        base: args.base,
        target: args.target,
        preserveGuides: guideMotion !== undefined,
    });
    const insertPatch = insertedPatches.insertPatch;
    const fadePatch = insertedPatches.fadePatch ?? {};

    phases.push({
        transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: {
            itemCollections: deriveInsertedTopologyWithSourceSharedConnectors({ base: args.base, target: args.target, settleGuides: guideMotion !== undefined }),
            constellations: args.target.constellations,
            itemParametricState: guideMotion ? {
                ...insertPatch,
                connectorItemByKey: {
                    ...guideMotion.settlePatch.itemParametricState?.connectorItemByKey,
                    ...insertPatch?.connectorItemByKey,
                },
            } : insertPatch,
            ...(args.target.constellationParametricState === undefined
                ? {}
                : { constellationParametricState: args.target.constellationParametricState }),
        },
    });

    if (
        Object.keys(fadePatch.entityItemByKey ?? {}).length > 0
        || Object.keys(fadePatch.connectorItemByKey ?? {}).length > 0
    ) {
        phases.push({
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey,
                duration: fadeInDurationMs,
            },
            patch: {
                itemParametricState: fadePatch,
            },
        });
    }

    const reanchorItemCollectionsPatch = deriveSharedConnectorReanchorPatch({
        base: {
            ...args.base,
            itemCollections: deriveInsertedTopologyWithSourceSharedConnectors({ base: args.base, target: args.target, settleGuides: guideMotion !== undefined }),
        },
        target: args.target,
    });
    if (reanchorItemCollectionsPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
                patch: { itemCollections: reanchorItemCollectionsPatch },
        });
    }

    return phases.length === 0 ? undefined : phases;
}

export function deriveLoopContinuationReverseTransitionPhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    pendingReplayMove: PendingReplayMove & { eventKind: 'loop_continuation'; direction: 'reverse' };
    config: Config;
    preItemTraceContext: PreItemTraceContext;
}): TransitionPhase[] | undefined {
    const phases: TransitionPhase[] = [];
    const transitionKey = deriveTransitionKey(args.pendingReplayMove);
    const guideMotion = derivePanelGuideMotion(args, false);
    const { spaceShiftDurationMs: contractDurationMs, fadeDurationMs: fadeOutDurationMs } = deriveLoopContinuationPhaseDurations(args.config);
    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    const rimCollectionKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimCollectionKey(args.preItemTraceContext.workflowKeyScope);
    const rimConstellationKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimConstellationKey(args.preItemTraceContext.workflowKeyScope);

    const reanchorItemCollectionsPatch = deriveSharedConnectorReanchorPatch({ base: args.base, target: args.target, preserveGuides: guideMotion !== undefined });
    if (reanchorItemCollectionsPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
                patch: { itemCollections: reanchorItemCollectionsPatch },
        });
    }

    const fadeOutPatch = deriveRemovedItemFadePatch({ base: args.base, target: args.target, preserveGuides: guideMotion !== undefined });

    if (hasInterpolatedPatchContent({ itemParametricState: fadeOutPatch })) {
        phases.push({
            transitionOptions: { transitionKind: 'interpolated', transitionKey, duration: fadeOutDurationMs },
                patch: { itemParametricState: fadeOutPatch },
        });
    }

    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.startPatch });
    const removalPatch = deriveRemovalPhasePatch({
        base: args.base, target: args.target,
        toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
        toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
        rimCollectionKey, rimConstellationKey,
    });
    const removalEffectivePatch = guideMotion
        ? { ...removalPatch, itemCollections: guideMotion.holdGuides(removalPatch.itemCollections ?? args.target.itemCollections) }
        : removalPatch;
    phases.push({
        transitionOptions: { transitionKind: 'instant', transitionKey },
        patch: removalEffectivePatch,
    });

    const contractPatch = mergeInterpolatedFramePatches(
        {
            constellationParametricState: deriveToolStepRingRadiiPatch({
                base: args.base,
                target: args.target,
                toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
                toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
                shapeSource: 'target',
            }),
        },
        { itemParametricState: deriveExistingCaseShapePatch({ base: args.base, target: args.target }) },
    );
    const contractWithRimPatch = mergeInterpolatedFramePatches(contractPatch, {
        itemParametricState: deriveExistingToolStepOuterRimPatch({
            base: args.base,
            target: args.target,
            rimItemKey,
        }),
    });
    const contractingScaffoldPatch = mergeInterpolatedFramePatches(contractWithRimPatch, guideMotion?.motionPatch ?? {});
    if (hasInterpolatedPatchContent(contractingScaffoldPatch)) {
        appendScaffoldPhases({
            phases,
            base: args.base,
            target: args.target,
            config: args.config,
            scaffoldPatch: contractingScaffoldPatch,
            durationMs: contractDurationMs,
            transitionKey,
            preItemTraceContext: args.preItemTraceContext,
            animationConfig: args.config.adapterModeConfig.playback.loopContinuationAnimation,
        });
    }
    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.settlePatch });

    const rimCleanupPatch = deriveRemovedOuterRimCleanupPatch({
        base: args.base,
        target: args.target,
        rimCollectionKey,
    });
    if (rimCleanupPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: rimCleanupPatch,
        });
    }

    const panelPatch = deriveSharedOutputPanelPatch({ base: args.base, target: args.target });
    if (panelPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
                patch: {
                    itemParametricState: panelPatch,
                    entityItemLabelChannelsToRemoveByKey: deriveWorkflowStateEntryLabelRemovals(args),
                },
        });
    }

    return phases.length === 0 ? undefined : phases;
}

export function deriveBranchExitRebindForwardTransitionPhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    pendingReplayMove: PendingReplayMove & { eventKind: 'branch_exit_rebind'; direction: 'forward' };
    config: Config;
    preItemTraceContext: PreItemTraceContext;
}): TransitionPhase[] | undefined {
    const phases: TransitionPhase[] = [];
    const transitionKey = deriveTransitionKey(args.pendingReplayMove);
    const guideMotion = derivePanelGuideMotion(args, false);
    const { spaceShiftDurationMs: contractDurationMs, fadeDurationMs: fadeOutDurationMs } = deriveBranchRebindPhaseDurations(args.config);
    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    const rimCollectionKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimCollectionKey(args.preItemTraceContext.workflowKeyScope);
    const rimConstellationKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimConstellationKey(args.preItemTraceContext.workflowKeyScope);
    const panelPatch = deriveSharedOutputPanelPatch({ base: args.base, target: args.target });
    if (panelPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: {
                itemParametricState: panelPatch,
                entityItemLabelChannelsToRemoveByKey: deriveWorkflowStateEntryLabelRemovals(args),
            },
        });
    }

    const reanchorItemCollectionsPatch = deriveSharedConnectorReanchorPatch({ base: args.base, target: args.target, preserveGuides: guideMotion !== undefined });
    if (reanchorItemCollectionsPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: { itemCollections: reanchorItemCollectionsPatch },
        });
    }

    const fadeOutPatch = deriveRemovedItemFadePatch({ base: args.base, target: args.target, preserveGuides: guideMotion !== undefined });
    if (hasInterpolatedPatchContent({ itemParametricState: fadeOutPatch })) {
        // Explicit fade opacity overrides activation dimming; freeze the rendered starting opacity first.
        const fadeStart = deriveRemovedItemFadeStartPatch(args);
        if (fadeStart !== undefined) {
            phases.push({
                transitionOptions: { transitionKind: 'instant', transitionKey },
                patch: { itemParametricState: fadeStart },
            });
        }
        phases.push({
            transitionOptions: { transitionKind: 'interpolated', transitionKey, duration: fadeOutDurationMs },
            patch: { itemParametricState: fadeOutPatch },
        });
    }

    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.startPatch });
    const removalPatch = deriveRemovalPhasePatch({
        base: args.base, target: args.target,
        toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
        toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
        rimCollectionKey, rimConstellationKey,
    });
    const removalEffectivePatch = guideMotion
        ? { ...removalPatch, itemCollections: guideMotion.holdGuides(removalPatch.itemCollections ?? args.target.itemCollections) }
        : removalPatch;
    phases.push({
        transitionOptions: { transitionKind: 'instant', transitionKey },
        patch: removalEffectivePatch,
    });

    const contractPatch = mergeInterpolatedFramePatches(
        {
            constellationParametricState: deriveToolStepRingRadiiPatch({
                base: args.base,
                target: args.target,
                toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
                toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
                shapeSource: 'target',
            }),
        },
        { itemParametricState: deriveExistingCaseShapePatch({ base: args.base, target: args.target }) },
    );
    const contractWithRimPatch = mergeInterpolatedFramePatches(contractPatch, {
        itemParametricState: deriveExistingToolStepOuterRimPatch({
            base: args.base,
            target: args.target,
            rimItemKey,
        }),
    });
    const contractingScaffoldPatch = mergeInterpolatedFramePatches(contractWithRimPatch, guideMotion?.motionPatch ?? {});
    if (hasInterpolatedPatchContent(contractingScaffoldPatch)) {
        appendScaffoldPhases({
            phases,
            base: args.base,
            target: args.target,
            config: args.config,
            scaffoldPatch: contractingScaffoldPatch,
            durationMs: contractDurationMs,
            transitionKey,
            preItemTraceContext: args.preItemTraceContext,
            animationConfig: args.config.adapterModeConfig.playback.branchExitRebindAnimation,
        });
    }
    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.settlePatch });

    const rimCleanupPatch = deriveRemovedOuterRimCleanupPatch({
        base: args.base,
        target: args.target,
        rimCollectionKey,
    });
    if (rimCleanupPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: rimCleanupPatch,
        });
    }

    return phases.length === 0 ? undefined : phases;
}

export function deriveBranchExitRebindReverseTransitionPhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    pendingReplayMove: PendingReplayMove & { eventKind: 'branch_exit_rebind'; direction: 'reverse' };
    config: Config;
    preItemTraceContext: PreItemTraceContext;
}): TransitionPhase[] | undefined {
    const phases: TransitionPhase[] = [];
    const transitionKey = deriveTransitionKey(args.pendingReplayMove);
    const guideMotion = derivePanelGuideMotion(args, true);
    const { spaceShiftDurationMs: expandDurationMs, fadeDurationMs: fadeInDurationMs } = deriveBranchRebindPhaseDurations(args.config);
    const rimItemKey = args.preItemTraceContext.workflowKeyScope === undefined
        ? undefined
        : buildToolStepOuterRimItemKey(args.preItemTraceContext.workflowKeyScope);
    const expandPatch = mergeInterpolatedFramePatches(
        {
            constellationParametricState: deriveToolStepRingRadiiPatch({
                base: args.base,
                target: args.target,
                toolStepCollectionKey: args.preItemTraceContext.toolStepCollectionKey,
                toolStepConstellationKey: args.preItemTraceContext.toolStepConstellationKey,
                shapeSource: 'base',
            }),
        },
        { itemParametricState: deriveExistingCaseShapePatch({ base: args.base, target: args.target }) },
    );
    const expandWithRimPatch = mergeInterpolatedFramePatches(expandPatch, {
        itemParametricState: deriveExistingToolStepOuterRimPatch({
            base: args.base,
            target: args.target,
            rimItemKey,
        }),
    });
    if (guideMotion) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey }, patch: guideMotion.startPatch });
    const expandingScaffoldPatch = mergeInterpolatedFramePatches(expandWithRimPatch, guideMotion?.motionPatch ?? {});
    if (hasInterpolatedPatchContent(expandingScaffoldPatch)) {
        appendScaffoldPhases({
            phases,
            base: args.base,
            target: args.target,
            config: args.config,
            scaffoldPatch: expandingScaffoldPatch,
            durationMs: expandDurationMs,
            transitionKey,
            preItemTraceContext: args.preItemTraceContext,
            animationConfig: args.config.adapterModeConfig.playback.branchExitRebindAnimation,
        });
    }

    const insertedPatches = deriveInsertedItemPatches({ base: args.base, target: args.target, preserveGuides: guideMotion !== undefined });
    phases.push({
        transitionOptions: { transitionKind: 'instant', transitionKey },
        patch: {
            itemCollections: deriveInsertedTopologyWithSourceSharedConnectors({ base: args.base, target: args.target, settleGuides: guideMotion !== undefined }),
            constellations: args.target.constellations,
            itemParametricState: guideMotion ? {
                ...insertedPatches.insertPatch,
                connectorItemByKey: {
                    ...guideMotion.settlePatch.itemParametricState?.connectorItemByKey,
                    ...insertedPatches.insertPatch?.connectorItemByKey,
                },
            } : insertedPatches.insertPatch,
            ...(args.target.constellationParametricState === undefined
                ? {}
                : { constellationParametricState: args.target.constellationParametricState }),
        },
    });

    if (hasInterpolatedPatchContent({ itemParametricState: insertedPatches.fadePatch })) {
        phases.push({
            transitionOptions: { transitionKind: 'interpolated', transitionKey, duration: fadeInDurationMs },
            patch: { itemParametricState: insertedPatches.fadePatch },
        });
    }

    const reanchorItemCollectionsPatch = deriveSharedConnectorReanchorPatch({ base: args.base, target: args.target });
    if (reanchorItemCollectionsPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: { itemCollections: args.target.itemCollections },
        });
    }

    const panelPatch = deriveSharedOutputPanelPatch({ base: args.base, target: args.target });
    if (panelPatch !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: {
                itemParametricState: panelPatch,
                entityItemLabelChannelsToRemoveByKey: deriveWorkflowStateEntryLabelRemovals(args),
            },
        });
    }

    const targetActivation = args.target.spaceRenderOptions?.activation;
    if (targetActivation !== undefined) {
        phases.push({
            transitionOptions: { transitionKind: 'instant', transitionKey },
            patch: { spaceRenderOptions: { activation: targetActivation } },
        });
    }

    return phases.length === 0 ? undefined : phases;
}
